import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useGetSubscriptionsLookupQuery, useGetUsersInfiniteQuery } from 'src/apiClients';
import type { UserListItem } from 'src/apiClients/queries';
import {
    SortDirection,
    type DataTableColDef,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';
import { ROUTES } from 'src/utils/strings';
import { createNaFallback } from 'src/utils/table-formatters';

import { createUserColumns, mapUserRow } from './user-table-formatters';
import { userTableI18n } from './user-table-formatters.i18n';
import { i18n } from './UsersPage.i18n';

const SEARCH_COLUMNS = ['login', 'email', 'id', 'seequentUserId'];
const INITIAL_SORT = [{ field: 'state', direction: SortDirection.Asc }];

export const UsersPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    // Small lookupfor customerName. Will be removed when backend embeds
    // subscription on user.
    const { data: subscriptions = [] } = useGetSubscriptionsLookupQuery();
    const customerNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const sub of subscriptions) map.set(sub.id, sub.customerName);
        return map;
    }, [subscriptions]);

    const mapRow = useCallback(
        (user: UserListItem): DataTableRow => ({
            ...mapUserRow(user),
            customerName: customerNameById.get(user.subscriptionId) ?? '',
            subscriptionId: user.subscriptionId,
        }),
        [customerNameById],
    );

    const columns = useMemo<DataTableColDef[]>(() => {
        const naFallback = createNaFallback(intl);
        const base = createUserColumns(intl);

        // Override login header label for top-level users page
        const loginCol = base.find((c) => c.field === 'login');
        if (loginCol) loginCol.headerName = intl.formatMessage(i18n.account);

        // Insert customerName after email
        const emailIdx = base.findIndex((c) => c.field === 'email');
        base.splice(emailIdx + 1, 0, {
            field: 'customerName',
            headerName: intl.formatMessage(i18n.customer),
            flex: 2,
            minWidth: 160,
            valueFormatter: naFallback,
            // Disable sort/search until backend embeds subscription on user.
            sortable: false,
        });

        return base;
    }, [intl]);

    const handleRowClick = useCallback(
        (row: DataTableRow) => {
            const userId = row.id;
            if (typeof userId === 'string' && userId !== '') {
                navigate(`${ROUTES.USERS}/${userId}`);
            }
        },
        [navigate],
    );

    return (
        <EntityListPage<UserListItem>
            automationId="users-page"
            useQuery={useGetUsersInfiniteQuery}
            searchColumns={SEARCH_COLUMNS}
            initialSort={INITIAL_SORT}
            columns={columns}
            mapRow={mapRow}
            onRowClick={handleRowClick}
            titleWhenNoResults={intl.formatMessage(userTableI18n.noUsersTitle)}
            messageWhenNoResults={intl.formatMessage(i18n.noUsersMessage)}
            titleWhenLoadError={intl.formatMessage(userTableI18n.loadErrorTitle)}
            messageWhenLoadError={intl.formatMessage(userTableI18n.loadErrorMessage)}
        />
    );
};
